// Legacy (3) + los 7 estilos del Design System (004). El estilo vive en la invitación
// (`template_id`); el render lo normaliza vía toEstilo().
export type TemplateId =
  | 'romantic' | 'minimalist' | 'rustic'
  | 'elegante' | 'rustico' | 'costero' | 'urbano' | 'romantico' | 'epoca' | 'moderno';

export interface ItineraryItem {
  time: string;
  title: string;
  description?: string;
}

export interface WeddingInvitation {
  id: string;
  user_id: string;
  event_id?: string | null;
  archived?: boolean;
  /** 004: JSONB de campos de sección del Design System sin columna propia. */
  sections?: Record<string, unknown> | null;
  bride_name: string;
  groom_name: string;
  /** 008 · US10 — orden de nombres en portada/hero (snapshot del evento). */
  couple_name_order?: string;
  wedding_date: string;
  template_id: TemplateId;
  primary_color: string;
  secondary_color: string;
  accent_color: string;
  
  // Ceremonias
  religious_ceremony_enabled: boolean;
  religious_ceremony_name?: string;
  religious_ceremony_address?: string;
  religious_ceremony_time?: string;
  religious_ceremony_map_url?: string;
  
  civil_ceremony_enabled: boolean;
  civil_ceremony_name?: string;
  civil_ceremony_address?: string;
  civil_ceremony_time?: string;
  civil_ceremony_map_url?: string;
  
  party_name?: string;
  party_address?: string;
  party_time?: string;
  party_map_url?: string;
  
  // Otros
  dresscode?: string;
  itinerary: ItineraryItem[];
  background_music_url?: string;
  background_music_enabled: boolean;
  children_allowed: boolean;
  
  // Regalos
  gifts_message?: string;
  bank_transfer_enabled: boolean;
  bank_account_holder?: string;
  bank_account_number?: string;
  bank_name?: string;
  cash_envelope_enabled: boolean;
  gift_registry_url?: string;
  qr_transfer_enabled?: boolean;
  qr_image_url?: string;

  // Galería
  couple_gallery_enabled: boolean;
  guest_photos_enabled: boolean;
  guest_photos_auto_approve: boolean; // 009: modo de aprobación del álbum
  
  // Meta
  unique_slug: string;
  is_published: boolean;
  is_paid: boolean;
  created_at: string;
  updated_at: string;
}

export type FamilyRole = 
  | 'bride_father' 
  | 'bride_mother' 
  | 'groom_father' 
  | 'groom_mother' 
  | 'godfather' 
  | 'godmother';

export interface InvitationFamily {
  id: string;
  invitation_id: string;
  name: string;
  role: FamilyRole;
  display_order: number;
  created_at: string;
}

export type RSVPStatus = 'pending' | 'confirmed' | 'declined';

export interface InvitationGuest {
  id: string;
  invitation_id: string;
  guest_name: string;
  /** 008 · US4 — rótulo de invitación (reemplaza el nombre en el saludo si está presente). */
  display_label?: string | null;
  guest_email?: string;
  guest_phone?: string;
  unique_access_code: string;
  passes_count: number;
  rsvp_status: RSVPStatus;
  rsvp_guests_count: number;
  rsvp_dietary_restrictions?: string;
  rsvp_message?: string;
  rsvp_responded_at?: string;
  /** 008 · US1 (cierre del deadline) — datos del evento para el aviso de "plazo cerrado". */
  rsvp_deadline?: string | null;
  coordinator_name?: string | null;
  coordinator_phone?: string | null;
  created_at: string;
  updated_at: string;
}

export interface InvitationGallery {
  id: string;
  invitation_id: string;
  photo_url: string;
  caption?: string;
  display_order: number;
  created_at: string;
}

export interface GuestPhoto {
  id: string;
  invitation_id: string;
  guest_id?: string;
  photo_url: string;
  caption?: string;
  is_approved: boolean;
  created_at: string;
}

export interface InvitationGift {
  id: string;
  invitation_id: string;
  gift_name: string;
  gift_description?: string;
  gift_image_url?: string;
  gift_price?: number;
  store_name?: string;
  store_url?: string;
  is_reserved: boolean;
  reserved_by_guest_id?: string;
  created_at: string;
}

export interface TemplateConfig {
  id: TemplateId;
  name: string;
  description: string;
  previewImage: string;
  defaultColors: {
    primary: string;
    secondary: string;
    accent: string;
  };
  fontFamily: {
    heading: string;
    body: string;
  };
}

export const TEMPLATES: TemplateConfig[] = [
  {
    id: 'romantic',
    name: 'Romántico Clásico',
    description: 'Elegante estilo con marco floral dorado y hojas de acuarela',
    previewImage: '/placeholder.svg',
    defaultColors: {
      primary: '#6B7B3F',      // Verde oliva (botones)
      secondary: '#FFFFFF',    // Blanco (fondo)
      accent: '#C9A962',       // Dorado (marcos y detalles)
    },
    fontFamily: {
      heading: 'Great Vibes',
      body: 'Montserrat',
    },
  },
  {
    id: 'minimalist',
    name: 'Minimalista Moderno',
    description: 'Líneas limpias, espacios amplios y sans-serif elegante',
    previewImage: '/placeholder.svg',
    defaultColors: {
      primary: '#2c3e50',
      secondary: '#ecf0f1',
      accent: '#e74c3c',
    },
    fontFamily: {
      heading: 'Montserrat',
      body: 'Open Sans',
    },
  },
  {
    id: 'rustic',
    name: 'Rústico Bohemio',
    description: 'Texturas naturales, colores tierra y elementos botánicos',
    previewImage: '/placeholder.svg',
    defaultColors: {
      primary: '#5d4e37',
      secondary: '#f5f0e8',
      accent: '#8fbc8f',
    },
    fontFamily: {
      heading: 'Amatic SC',
      body: 'Josefin Sans',
    },
  },
];

export const FAMILY_ROLE_LABELS: Record<FamilyRole, string> = {
  bride_father: 'Padre de la Novia',
  bride_mother: 'Madre de la Novia',
  groom_father: 'Padre del Novio',
  groom_mother: 'Madre del Novio',
  godfather: 'Padrino',
  godmother: 'Madrina',
};
